import { Box, Typography, Stack, Paper, useTheme } from '@mui/material';
import { useIntl } from 'react-intl';

import { useDatasetSummary } from './useDatasetSummary';

interface RowProps {
  label: string;
  value: number;
  color: string;
}
const Row = ({ label, value, color }: RowProps) => (
  <Stack direction="row" alignItems="center" justifyContent="space-between" mt={2}>
    <Stack direction="row" alignItems="center" spacing={1}>
      <Box
        sx={{
          width: 10,
          height: 10,
          backgroundColor: color,
        }}
      />
      <Typography variant="body1">{label}</Typography>
    </Stack>

    <Typography fontWeight={600}>{value.toLocaleString()}</Typography>
  </Stack>
);

interface DatasetSummaryProps {
  validationEncounterCount: number;
  testEncounterCount: number;
  trainingEncounterCount: number;
}

export const DatasetSummary = ({
  validationEncounterCount,
  testEncounterCount,
  trainingEncounterCount,
}: DatasetSummaryProps) => {
  const { validationPercent, trainingPercent, testPercent, total } = useDatasetSummary({
    validationEncounterCount,
    testEncounterCount,
    trainingEncounterCount,
  });
  const theme = useTheme();
  const { formatMessage } = useIntl();
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(3) }}>
      <Typography variant="h6">
        {formatMessage({ id: 'createProjectWizard.step.reviewAndCreate.datasetSummary.title' })}
      </Typography>
      <Paper id="dataset-summary" sx={{ p: theme.spacing(4) }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant="body1">
            {formatMessage({
              id: 'createProjectWizard.step.reviewAndCreate.datasetSummary.totalEncounters',
            })}
          </Typography>
          <Typography variant="h5" fontWeight={700}>
            {' '}
            {total.toLocaleString()}{' '}
          </Typography>
        </Stack>

        <Stack direction="row" height={10} overflow="hidden" mb={3}>
          <Box
            sx={{ width: `${validationPercent}%`, backgroundColor: theme.palette.primary.dark }}
          />
          <Box
            sx={{ width: `${trainingPercent}%`, backgroundColor: theme.palette.secondary.main }}
          />
          <Box sx={{ width: `${testPercent}%`, backgroundColor: theme.palette.primary.light }} />
        </Stack>

        <Row
          label={formatMessage({
            id: 'createProjectWizard.step.reviewAndCreate.datasetSummary.validationEncounters',
          })}
          value={validationEncounterCount}
          color={theme.palette.primary.dark}
        />

        <Row
          label={formatMessage({
            id: 'createProjectWizard.step.reviewAndCreate.datasetSummary.trainingEncounters',
          })}
          value={trainingEncounterCount}
          color={theme.palette.secondary.main}
        />

        <Row
          label={formatMessage({
            id: 'createProjectWizard.step.reviewAndCreate.datasetSummary.testEncounters',
          })}
          value={testEncounterCount}
          color={theme.palette.primary.light}
        />
      </Paper>
    </Box>
  );
};
